/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {
  DEFAULT_PAGINATION,
  jobPaginationState,
  setJobPaginationState,
  TabPagination
} from './signals/paginationState';
import {initialPerTabState, PermanentTabs} from './signals/state';

// resetting a tab keeps whatever page size the user chose for it, so returning
// to a tab restarts at page 1 without also discarding their rows-per-page...
const resetEntry = (previous?: TabPagination): TabPagination => ({
  page     : DEFAULT_PAGINATION.page,
  pageSize : previous?.pageSize ?? DEFAULT_PAGINATION.pageSize
});

/**
 * Custom hook: useJobPagination
 *
 * Provides utilities to manage pagination state per tab, following
 * a similar pattern to the facets handling (useJobSelectedFacets).
 *
 * It supports both permanent and dynamic tabs, allowing you to:
 * - Get the current pagination state for a tab.
 * - Update the full pagination (page, pageSize) for a tab.
 * - Reset pagination for a specific tab while preserving pageSize.
 * - Remove pagination state for dynamic tabs or reset for permanent ones.
 * - Clear all pagination and restore defaults for permanent tabs.
 *
 * This ensures consistency between filters (facets) and pagination behavior
 * across tabs, especially when switching context or resetting state.
 *
 * Returns:
 * {
 *   jobPaginationState: reactive signal holding pagination per tab,
 *   getPaginationForTab: retrieves pagination for a tab,
 *   setPaginationForTab: sets full pagination state for a tab,
 *   clearPaginationForTab: resets pagination for a tab,
 *   removePaginationForTab: removes or resets tab pagination,
 *   clearAllPagination: resets all tabs to default values
 * }
 */
export const useJobPagination = () => {
  /**
   * Retrieves the pagination state for a given tab.
   * @param tab The tab identifier.
   * @returns The pagination object (page, pageSize) for the tab.
   */
  const getPaginationForTab = useCallback((tab: string) => {
    return jobPaginationState.value[tab];
  }, []);

  /**
   * Sets the full pagination state for a tab.
   * Overwrites both page and pageSize.
   * @param tab The tab identifier.
   * @param payload The pagination values to apply.
   */
  const setPaginationForTab = useCallback((
    tab: string,
    {
      page,
      pageSize
    }: TabPagination) => {
    setJobPaginationState({
      ...jobPaginationState.value,
      [tab] : {page, pageSize}
    });
  }, []);

  /**
   * Resets pagination for a tab to default values.
   * - Keeps the tab
   * - Preserves previous pageSize if available
   * @param tab The tab identifier.
   */
  const clearPaginationForTab = useCallback((tab: PermanentTabs | string): void => {
    const current = {...jobPaginationState.value};
    current[tab]  = resetEntry(current[tab]);
    setJobPaginationState(current);
  }, []);

  /**
   * Removes or resets pagination state for a tab depending on its type.
   * - Permanent tabs are reset to defaults.
   * - Dynamic tabs are completely removed.
   * @param tab The tab identifier.
   */
  const removePaginationForTab = useCallback((tab: PermanentTabs | string): void => {
    const current     = {...jobPaginationState.value};
    const isPermanent = Object.values(PermanentTabs).includes(tab as PermanentTabs);
    if (isPermanent) {
      current[tab] = resetEntry(current[tab]);
    } else {
      delete current[tab];
    }
    setJobPaginationState(current);
  }, []);

  /**
   * Completely resets the pagination signal.
   * - Removes all pagination state for dynamic tabs.
   * - Resets pagination for permanent tabs to default values.
   * - Effectively reinitializes the entire pagination state.
   */
  const clearAllPagination = useCallback(() => {
    setJobPaginationState(initialPerTabState(() => ({...DEFAULT_PAGINATION})));
  }, []);

  return {
    jobPaginationState,
    getPaginationForTab,
    setPaginationForTab,
    clearPaginationForTab,
    removePaginationForTab,
    clearAllPagination
  };
};

export default useJobPagination;
